import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import placeholderImg from '../../assets/ad-placeholder.png';
import { useLiveEditor } from '../../contexts/LiveEditorContext';

interface AdContentProps {
  title: string;
  description: string;
  imageUrl?: string;
  ctaText?: string;
}

export const AdContent: React.FC<AdContentProps> = ({ title, description, imageUrl, ctaText }) => {
  const { t } = useTranslation();
  const { appendToEditor, editedResult } = useLiveEditor();
  const [imageError, setImageError] = useState(false);
  const [hoveredText, setHoveredText] = useState<string | null>(null);
  const [addedTexts, setAddedTexts] = useState<string[]>([]);
  const buttonText = ctaText || t('ads.learn_more');

  // Listen for when the editor is cleared
  useEffect(() => {
    if (!editedResult.trim()) {
      setAddedTexts([]);
    }
  }, [editedResult]);

  const handleAddToEditor = (text: string) => {
    appendToEditor(text);
    setAddedTexts((prev) => [...prev, text]);
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-col md:flex-row gap-4 items-start">
        {imageUrl && !imageError ? (
          <div className="w-full md:w-1/2">
            <img
              src={imageUrl}
              alt="Ad visual"
              className="w-full rounded-md"
              onError={() => setImageError(true)}
            />
          </div>
        ) : (
          <div className="w-full md:w-1/2">
            <img src={placeholderImg} alt="Ad visual" className="w-full rounded-md" />
          </div>
        )}
        <div className="w-full md:w-1/2 flex flex-col">
          <div
            className={`relative p-1 border ${
              addedTexts.includes(title)
                ? 'border-green-500'
                : hoveredText === title
                  ? 'border-blue-300'
                  : 'border-transparent'
            } rounded-md`}
            onMouseEnter={() => setHoveredText(title)}
            onMouseLeave={() => setHoveredText(null)}
          >
            <p className="text-gray-800 dark:text-gray-200 font-medium">{title}</p>
            {hoveredText === title && (
              <button
                className="absolute top-1 right-1 bg-blue-500 text-white text-xs px-2 py-1 rounded hover:bg-blue-600 flex items-center gap-1"
                onClick={() => handleAddToEditor(title)}
              >
                {addedTexts.includes(title) && <span className="text-white">✓</span>}
                {t('common.add_to_live_editor')}
              </button>
            )}
          </div>
          <div
            className={`relative mt-1 p-1 border ${
              addedTexts.includes(description)
                ? 'border-green-500'
                : hoveredText === description
                  ? 'border-blue-300'
                  : 'border-transparent'
            } rounded-md`}
            onMouseEnter={() => setHoveredText(description)}
            onMouseLeave={() => setHoveredText(null)}
          >
            <p className="text-gray-600 dark:text-gray-400 text-sm">{description}</p>
            {hoveredText === description && (
              <button
                className="absolute top-1 right-1 bg-blue-500 text-white text-xs px-2 py-1 rounded hover:bg-blue-600 flex items-center gap-1"
                onClick={() => handleAddToEditor(description)}
              >
                {addedTexts.includes(description) && <span className="text-white">✓</span>}
                {t('common.add_to_live_editor')}
              </button>
            )}
          </div>
          <button className="mt-3 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-md px-4 py-1 text-sm self-start">
            {buttonText}
          </button>
        </div>
      </div>
    </div>
  );
};
